import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import {
  AccessibleNavigation,
  MainContentLandmark,
  CommandPalette,
  SpatialNavigationProvider,
} from "@/components/navigation";
import { useMultiViewportNavigation } from "@/hooks/navigation";
import { keyboardShortcuts } from "@/utils/navigation";
import { useAuthActions } from "@/stores/auth/auth-store";
import { useAuthStore } from "@/stores/auth/auth-store";
import { NavigationDrawer as SharedNavigationDrawer } from "./NavigationDrawer";
import { Footer } from "./Footer";
import { OrganizationContextSwitcher } from "@/components/organization/OrganizationContextSwitcher";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import { setAppNavigationHandler } from "@/components/notifications/notificationNavigation";
import { config } from "@/config";
import { socketClient } from "@/services/websocket/socketClient";
import { setupNotificationWebSocketListeners } from "@/stores/notifications/notification-store";
import { TerraMatrixBrand } from "@/components/branding/TerraMatrixBrand";

interface RootLayoutProps {
  children: React.ReactNode;
}

const ROOT_LAYOUT_SOCKET_OWNER = "root-layout";

const TEXT_SELECTION_ALLOWED_SELECTOR = [
  "input",
  "textarea",
  "select",
  "[contenteditable='true']",
  "[role='textbox']",
  "[data-allow-text-selection='true']",
].join(",");

export const RootLayout: React.FC<RootLayoutProps> = ({ children }) => {
  const {
    navigationState,
    toggleDrawer,
    closeDrawer,
  } = useMultiViewportNavigation();

  const { logout } = useAuthActions();
  const { user, tokens, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  const handleSignOut = () => {
    logout();
    navigate("/");
  };

  const handleAppShellMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }

    const target = event.target;
    if (
      target instanceof Element &&
      target.closest(TEXT_SELECTION_ALLOWED_SELECTOR)
    ) {
      return;
    }

    event.preventDefault();
  };

  // Register additional keyboard shortcuts
  React.useEffect(() => {
    keyboardShortcuts.register({
      id: "toggle-navigation",
      keys: ["meta", "b"],
      description: "Toggle navigation sidebar",
      category: "Navigation",
      action: toggleDrawer,
    });

    return () => {
      keyboardShortcuts.unregister(["meta", "b"]);
    };
  }, [toggleDrawer]);

  React.useEffect(() => {
    setupNotificationWebSocketListeners();
  }, []);

  React.useEffect(() => {
    return setAppNavigationHandler((target) => {
      navigate(target);
    });
  }, [navigate]);

  React.useEffect(() => {
    if (!isAuthenticated || !user?.id || !tokens?.accessToken) {
      socketClient.releaseConnection(ROOT_LAYOUT_SOCKET_OWNER);
      return;
    }

    void socketClient
      .acquireConnection(ROOT_LAYOUT_SOCKET_OWNER, {
        url: config.websocket.url,
        token: tokens.accessToken,
        userId: user.id,
        reconnectionAttempts: config.websocket.reconnectAttempts,
        reconnectionDelay: config.websocket.reconnectInterval,
      })
      .catch((error: unknown) => {
        console.warn(
          "[RootLayout] Failed to initialize notification WebSocket",
          error instanceof Error ? error.message : String(error)
        );
      });

    return () => {
      socketClient.releaseConnection(ROOT_LAYOUT_SOCKET_OWNER);
    };
  }, [isAuthenticated, tokens?.accessToken, user?.id]);

  return (
    <SpatialNavigationProvider
      config={{ enabled: true, focusRingVisible: true }}
    >
      <AccessibleNavigation>
        <div
          className="tm-app-shell min-h-screen bg-background flex flex-col"
          onMouseDown={handleAppShellMouseDown}
        >
          {/* Header */}
          <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="container mx-auto px-4 py-3">
              <div className="flex items-center justify-between">
                {/* Logo and Hamburger Menu */}
                <div className="flex items-center gap-3">
                  {/* Hamburger Menu Button */}
                  <button
                    onClick={toggleDrawer}
                    className="p-2 hover:bg-accent rounded-md transition-colors"
                    aria-label="Toggle navigation menu"
                    aria-expanded={navigationState.isDrawerOpen}
                  >
                    <Menu className="h-5 w-5" />
                  </button>

                  <Link
                    to="/dashboard"
                    className="flex items-center gap-2.5 hover:opacity-90 transition-opacity"
                  >
                    <TerraMatrixBrand
                      markClassName="h-7 w-7"
                      nameClassName="text-lg"
                    />
                  </Link>
                </div>


                {/* Actions */}
                <div className="flex items-center gap-2">
                  <OrganizationContextSwitcher />
                  <NotificationCenter />
                </div>
              </div>

            </div>
          </header>

          {/* Navigation Drawer */}
          <SharedNavigationDrawer
            isOpen={navigationState.isDrawerOpen}
            onClose={closeDrawer}
            onSignOut={handleSignOut}
          />

          {/* Main Content */}
          <div className="flex-1">
            <MainContentLandmark>
              <div className="container mx-auto px-4 py-6 w-full">
                {children}
              </div>
            </MainContentLandmark>
          </div>

          {/* Footer */}
          <Footer />

          {/* Command Palette */}
          <CommandPalette />
        </div>
      </AccessibleNavigation>
    </SpatialNavigationProvider>
  );
};
